import moment from 'moment'

/**
 * Форматирует дату для отображения в UI.
 * Принимает: Moment-объект, строку 'YYYY-MM-DD', Date, null
 * Возвращает: строку 'DD.MM.YYYY'
 */
export const formatDisplay = (value) => {
  if (!value) return '';

  // Если это строка 'YYYY-MM-DD'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value.split('-');
    return `${day}.${month}.${year}`;
  }

  // Если это Moment-объект
  if (value && typeof value.format === 'function') {
    return value.format('DD.MM.YYYY');
  }

  // Если это Date
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${day}.${month}.${year}`;
  }

  return '';
};

/**
 * Добавляет дни к дате.
 * Принимает: Moment, строку 'YYYY-MM-DD', Date
 * Возвращает: строку 'YYYY-MM-DD'
 */
export const addDays = (value, days) => {
  if (!value) return null;

  // Если это Moment
  if (value && typeof value.add === 'function') {
    return value.clone().add(days, 'days').format('YYYY-MM-DD');
  }

  // Если это строка или Date
  const date = new Date(value);
  date.setDate(date.getDate() + days);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * Считает разницу в днях между двумя датами.
 * Возвращает: число дней (положительное)
 */
export const diffDays = (start, end) => {
  if (!start || !end) return 0;

  // Если оба Moment
  if (start && typeof start.diff === 'function') {
    return Math.abs(start.diff(end, 'days'));
  }

  // Если строки или Date
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffTime = Math.abs(endDate - startDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
/**
 *  a-b
 * @param {string|Date|import('moment').Moment} a 
 * @param {string|Date|import('moment').Moment} b 
 * @returns Number
 */
export const diffDaysNoModal = (a, b) => {
  if (!a || !b) return 0;
  // Если строки или Date
    if (a && typeof a.diff === 'function') {
    return a.diff(b, 'days');
  }
  const startDate = new Date(a);
  const endDate = new Date(b);
  const diffTime = startDate - endDate;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

export const getDatesDiff = (dateStrA, dateStrB, unit = 'days') =>{
  if (!dateStrA || !dateStrB) return 0;
  const msA = new Date(dateStrA).getTime();
  const msB = new Date(dateStrB).getTime();
  const diffInMs = msA - msB;
  const MS_PER_DAY = 1000 * 60 * 60 * 24;
  const MS_PER_WEEK = MS_PER_DAY * 7;
  if (unit === 'weeks') {
    return diffInMs / MS_PER_WEEK;
  }
  return diffInMs / MS_PER_DAY;
}


/**
 * Проверяет, находится ли дата в диапазоне.
 * Возвращает: true/false
 */
export const isDateBetween = (date, startDate, endDate) => {
  if (!date || !startDate || !endDate) return false;

  // Если это Moment
  if (date && typeof date.isBetween === 'function') {
    return date.isBetween(startDate, endDate, null, '[]');
  }

  // Если строки — просто сравниваем лексикографически
  return date >= startDate && date <= endDate;
};

/**
 * Превращает дату в timestamp (миллисекунды) для вычислений.
 * Используется в createSelector для расчета позиций в Ганте.
 */
export const toTimestamp = (value) => {
  if (!value) return 0;

  // Если это Moment
  if (value && typeof value.valueOf === 'function') {
    return value.valueOf();
  }

  // Если строка или Date
  return new Date(value).getTime();
};

export const isValidDate = (value) => {
  if (!value) return false;

  // Если это Moment
  if (value && typeof value.isValid === 'function') {
    return value.isValid();
  }

  // Если это строка 'YYYY-MM-DD'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const d = new Date(value);
    return !isNaN(d.getTime());
  }

  // Если это Date
  if (value instanceof Date) {
    return !isNaN(value.getTime());
  }

  return false;
};

export const minDate = (date1, date2) => {
  if (!isValidDate(date1)) return date2;
  if (!isValidDate(date2)) return date1;

  // Если это Moment-объекты — возвращаем Moment
  if (date1 && typeof date1.clone === 'function') {
    return moment.min([date1, date2]);
  }

  // Если это строки 'YYYY-MM-DD' — возвращаем строку
  if (typeof date1 === 'string' && typeof date2 === 'string') {
    return date1 <= date2 ? date1 : date2;
  }

  // Если это Date — возвращаем новый Date
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1 <= d2 ? new Date(d1) : new Date(d2);
};

export const maxDate = (date1, date2) => {
  if (!isValidDate(date1)) return date2;
  if (!isValidDate(date2)) return date1;

  // Если это Moment-объекты — возвращаем Moment
  if (date1 && typeof date1.clone === 'function') {
    return moment.max([date1, date2]);
  }

  // Если это строки 'YYYY-MM-DD' — возвращаем строку
  if (typeof date1 === 'string' && typeof date2 === 'string') {
    return date1 >= date2 ? date1 : date2;
  }

  // Если это Date — возвращаем новый Date
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  return d1 >= d2 ? new Date(d1) : new Date(d2);
};

export const daysUntilSunday = (dateStr) => {
  if (!dateStr) return 0;
  const date = new Date(dateStr);
  const dayOfWeek = date.getDay(); // 0=воскресенье, 1=понедельник, ..., 6=суббота
  return (7 - dayOfWeek) % 7;
};

export const formDateSelector = (formName, field) => {
  return (state) => {
    const value = state.form?.[formName]?.values?.[field];
    return toDateString(value);
  };
};

export const toDateString = (value) => {
  if (!value) return null;

  // Уже строка 'YYYY-MM-DD'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }

  // Moment-объект
  if (value && typeof value.format === 'function') {
    return value.format('YYYY-MM-DD');
  }

  // Date
  if (value instanceof Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  return null;
};

export const todayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (dateStr) => {
  if (!dateStr) return null;

  if (dateStr instanceof Date) {
    return isNaN(dateStr.getTime()) ? null : dateStr;
  }

  if (dateStr && typeof dateStr.toDate === 'function') {
    return dateStr.toDate();
  }

  if (typeof dateStr === 'string') {
    const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const [, yearStr, monthStr, dayStr] = match;
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10) - 1; // в JS месяцы 0-11
    const day = parseInt(dayStr, 10);

    // Создаём Date в локальном часовом поясе
    const date = new Date(year, month, day);

    // Проверяем, что дата валидна (например, 2022-02-30 не существует)
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  return null;
};

const DATE_FIELDS = ['start', 'end', 'startPlan', 'endPlan'];
export const enrichTaskWithMoments = (task) => {
  if (!task) return null;
  const enriched = { ...task };
  DATE_FIELDS.forEach((field) => {
    if (enriched[field]) {
      enriched[field] = moment(enriched[field]);
    }
  });
  return enriched;
};


export const getMinMaxDates = (datesArray) => {
  let min = null;
  let max = null;
  for (let i = 0; i < datesArray.length; i++) {
    const date = datesArray[i];
    if (!date) continue;
    if (!min || date < min) min = date;
    if (!max || date > max) max = date;
  }
  return { min, max };
};

export const getMinMaxFromObjects = (items, getDate) => {
  let min = null;
  let max = null;
  for (let i = 0; i < items.length; i++) {
    const date = getDate(items[i]);
    if (!date) continue;
    if (!min || date < min) min = date;
    if (!max || date > max) max = date;
  }
  return { min, max };
};

export const getGanttDateRange = (tasks) => {
  let min = null;
  let max = null;
  for (let i = 0; i < tasks.length; i++) {
    const task = tasks[i];
    if (!task) continue;
    if (task.start) {
      if (!min || task.start < min) min = task.start;
      if (!max || task.start > max) max = task.start;
    }
    if (task.end) {
      if (!min || task.end < min) min = task.end;
      if (!max || task.end > max) max = task.end;
    }
  }
  return { min, max };
};